import React, { useEffect, useMemo, useState } from 'react';
import './ReduceAction.css'
import { notification } from "antd";
import { COMMITTED, COMPLETED, VIEWED, shuffle, COMMITTED_ENGAGEMENT_CATEGORY, categorizeByPriority } from './reduceActionsUtils';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import * as ccAnalytics from "../../../util/analytics/ccAnalytics";
import CCButton from '../../common/CCButtonV3';

import imgDiet from "./media/action-diet.png";
import imgDriving from "./media/action-driving.png";
import imgEnergy from "./media/action-energy.png";
import imgFlight from "./media/action-flight.png";
import imgHousehold from "./media/action-household.png";
import imgWaste from "./media/action-waste.png";
import { firstLetterUpperCase } from "../../../util/stringFormatter";
import CheckedIcon from "../../../images/icons/checked.svg";

const NEXT = 'NEXT';
const PREV = 'PREV';

const isActionCommitted = (a) => COMMITTED_ENGAGEMENT_CATEGORY.includes(a?.mostRecentRelevantEngagement?.type);

const COMMIT_CTA_VARIANTS = [
    "Count me in!",
    "I'll do it!",
    "I'm all in!",
    "I'm committed!",
    "I'm in it!",
    "I'm on board!",
    "I'm ready!",
    "I'm with you!",
    "Join the cause!",
    "Let's commit!",
    "Let's do this!",
    "Let's get started!",
    "Let's join forces!",
    "Let's make a difference!",
    "Let's make it happen!",
    "Ready to act!",
    "Sign me up!",
    "Take action!",
    "Yes, I'll help!",
]

shuffle(COMMIT_CTA_VARIANTS); // shuffle once per application

const IconButton = ({ icon, onClick }) => {
  return (
    <div className='p-2 cursor-pointer' onClick={onClick}>
      <FontAwesomeIcon icon={icon} />
    </div>
  );
};

const ReduceAction = ({ reduceActions, onUserEngagedWithReduceAction, title }) => {
  if (!reduceActions) {
    return null;
  }

  const [committedBefore, setCommitedBefore] = useState(null);

  const reduceActionsWithCta = useMemo(() => {
    let mapped = reduceActions.map( (a,i) => ({...a, commitCta: COMMIT_CTA_VARIANTS[i % COMMIT_CTA_VARIANTS.length]}));
    let categorized = categorizeByPriority(mapped);

    let idsToShowInTheEnd;
    if (!committedBefore) {
      idsToShowInTheEnd = categorized.filter(isActionCommitted).map(a => a.id)
      setCommitedBefore(idsToShowInTheEnd)
    } else {
      idsToShowInTheEnd = committedBefore;
    }
    // Put committed/completed actions in the end, while still sorting by priority
    categorized.sort((a,b) => (idsToShowInTheEnd.includes(a.id) - idsToShowInTheEnd.includes(b.id)) || (b.priority - a.priority));
    return categorized;
  }, [reduceActions]);


  return (
    <div style={{ width: '100%', margin: '0 auto' }}>
      <Slider slides={reduceActionsWithCta} onUserEngagedWithReduceAction={onUserEngagedWithReduceAction} title={title} />
    </div>
  );
};

const Slider = ({ slides, onUserEngagedWithReduceAction, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [actionState, setActionState] = useState(null);
  const [isPending, setIsPending] = useState(false);

  if (!slides) {
    return null;
  }

  const currentSlide = slides[currentIndex];
  const time = 200;

  const clearState = () => {
    setIsPending(false);
    setActionState(null);
  };

  const goToPrevious = (state) => {
    setIsPending(true);
    setActionState(state);
    setTimeout(() => {
      const isFirstSlide = currentIndex === 0;
      const newIndex = isFirstSlide ? slides.length - 1 : currentIndex - 1;
      setCurrentIndex(newIndex);
      setTimeout(() => clearState(), time);
    }, time);
  };

  const goToNext = (state) => {
    setIsPending(true);
    setActionState(state);
    setTimeout(() => {
      const isLastSlide = currentIndex === slides.length - 1;
      const newIndex = isLastSlide ? 0 : currentIndex + 1;
      setCurrentIndex(newIndex);
      setTimeout(() => clearState(), time);
    }, time);
  };

  useEffect(() => {
    if (currentSlide) {
      onUserEngagedWithReduceAction({ reduceAction: currentSlide, engagementType: VIEWED });
    }
  }, [currentSlide]);

  const handleAction = (state) => {
    setActionState(state);
    if (state == COMMITTED) {
      notification.success({
        message: 'Committed To Action',
        description: currentSlide?.title,
        placement: 'topRight',
      });
      ccAnalytics.event({
        category: 'reduce-action-commit-cta',
        action: 'click',
        label: currentSlide.commitCta,
      });
    }
    setTimeout(() => {
      setIsPending(true);
      onUserEngagedWithReduceAction({ reduceAction: currentSlide, engagementType: state });
      const isLastSlide = currentIndex === slides.length - 1;
      const newIndex = isLastSlide ? 0 : currentIndex + 1;
      setCurrentIndex(newIndex);
      setTimeout(() => clearState(), time);
    }, time);
  };

  const slideAnimationClass = useMemo(() => {
    if (isPending && [COMMITTED,COMPLETED,NEXT].includes(actionState)) {
      return 'fade-out-left';
    } else if (actionState == PREV && isPending) {
      return 'fade-out-right';
    } else return 'fade-in';
  }, [isPending, actionState]);

  return (
    <div className='bg-accent-light p-4 d-flex w-100 h-100 d-column justify-between'>
      <div>
        <div className='mb-4 d-flex justify-between align-center flex-wrap'>
          <h4 className='m-0'>{title || "Climate action tips"}</h4>
          <div className='d-flex align-center gap-1' style={{ color: '#999594' }}>
            <IconButton icon={faChevronLeft} onClick={() => goToPrevious(PREV)} />
            <p className='m-0'>Tip {currentIndex + 1}/{slides.length}</p>
            <IconButton icon={faChevronRight} onClick={() => goToNext(NEXT)} />
          </div>
        </div>

        <div className={'bg-white p-4 border-light d-column justify-between ' + slideAnimationClass} style={{ minHeight: 240 }}>
          <div className='d-flex gap-3 tip-content'>
            <div>
              <h5>{currentSlide?.title}</h5>
              <p className='m-0'>
                <small className='mb-1 d-block color-grey-light'>
                  {firstLetterUpperCase(currentSlide.relatedFootprintQuestionAlias)}
                  {currentSlide?.priorityGroup && <span className='mx-2'>•</span>}
                  {currentSlide?.priorityGroup?.label}{currentSlide?.priorityGroup?.textIcon}
                </small>
              </p>
              <p className='m-0'>{currentSlide?.description}.</p>
            </div>

            <div>
              {currentSlide?.relatedFootprintQuestionAlias == 'DIET' ? (
                <img src={imgDiet} className='center' />
              ) : currentSlide?.relatedFootprintQuestionAlias == 'DRIVING' ? (
                <img src={imgDriving} className='center' />
              ) : currentSlide?.relatedFootprintQuestionAlias == 'ENERGY' ? (
                <img src={imgEnergy} className='center' />
              ) : currentSlide?.relatedFootprintQuestionAlias == 'FLIGHTS' ? (
                <img src={imgFlight} className='center' />
              ) : currentSlide?.relatedFootprintQuestionAlias == 'WASTE' ? (
                <img src={imgWaste} className='center' />
              ) : currentSlide?.relatedFootprintQuestionAlias == 'HOUSEHOLD' ? (
                <img src={imgHousehold} className='center' />
              ) : (
                ''
              )}
            </div>
          </div>

          <div className='mt-4 flex-wrap gap-1'>
            {COMMITTED_ENGAGEMENT_CATEGORY.includes(currentSlide?.mostRecentRelevantEngagement?.type) ? (
              <p className='m-0 color-primary text-bold align-center d-flex'>
                <img src={CheckedIcon} alt='Tip status icon - checked' className='mr-1' style={{ width: 32, height: 32 }} />
                {firstLetterUpperCase(currentSlide.mostRecentRelevantEngagement.type)}
              </p>
            ) : (<>
              <CCButton buttonName="I'm already doing this" type='default' size='large' onClickButton={() => handleAction(COMPLETED)} />
              <CCButton buttonName={currentSlide.commitCta} type='primary' size='large' onClickButton={() => handleAction(COMMITTED)} />
            </>)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReduceAction;